import React, { useState, useRef } from 'react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Upload, X, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

export function LogoUploader({ label, value, onChange, className = '' }) {
  // Construir URL absoluta se value for caminho relativo
  const getAbsoluteUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('data:')) return url; // Data URL (preview local)
    if (url.startsWith('http')) return url; // Já é absoluta
    // Caminho relativo - construir URL absoluta
    const apiUrl = import.meta.env.VITE_API_URL || 'https://api.algti.com';
    return `${apiUrl}${url}`;
  };

  const [preview, setPreview] = useState(getAbsoluteUrl(value));
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo
    if (!['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml'].includes(file.type)) {
      toast.error('Formato inválido. Use PNG, JPG ou SVG.');
      return;
    }

    // Validar tamanho (2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Arquivo muito grande. Máximo 2MB.');
      return;
    }

    // Criar preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
      onChange(file);
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setPreview(null);
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className={className}>
      {label && <Label className="text-xs text-slate-600 mb-2">{label}</Label>}
      
      {preview ? (
        <div className="relative inline-block">
          <div className="w-32 h-32 rounded-lg border-2 border-slate-200 bg-white flex items-center justify-center overflow-hidden">
            <img src={preview} alt="Logo preview" className="max-w-full max-h-full object-contain" />
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="w-32 h-32 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex flex-col items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon className="w-8 h-8 text-slate-400 mb-2" />
          <span className="text-xs text-slate-500">Clique para enviar</span>
          <span className="text-xs text-slate-400 mt-1">PNG, JPG ou SVG</span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/jpg,image/svg+xml"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="mt-2 text-xs text-slate-500">
        Tamanho recomendado: 200x200px • Máx: 2MB
      </div>
    </div>
  );
}
